import React from "react";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import CommonButtons from "src/shared/components/CommonButtons";
import { Order } from "src/shared/services/interfacev2";

interface CancelOrderDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order;
  outstandingAmount: number;
  cancelData: string;
  onCancelDataChange: (value: string) => void;
  onConfirmCancel: () => void;
}

const CancelOrderDialog: React.FC<CancelOrderDialogProps> = ({ open, onClose, order, outstandingAmount, cancelData, onCancelDataChange, onConfirmCancel }) => {
  // Hooks and calculations first
  const receivedAmount = order?.orders?.reduce((total, order) => total + Number(order?.receivedAmount || 0), 0) || 0;
  const ledgerReceivedAmount = order?.orders?.reduce((total, order) => total + Number(order?.ledgerReceivedAmount || 0), 0) || 0;
  const remainingAmount = Number(order?.orders[0].remainingAmount || 0);

  // useCallback MUST be called before any early return
  const refundAmount = React.useCallback(() => {
    if (outstandingAmount >= receivedAmount) return 0;
    return receivedAmount - outstandingAmount;
  }, [outstandingAmount, receivedAmount]);

  // Early return if modal closed
  if (!open) return null;

  const deductDetails = [
    { label: "Outstanding Balance", value: outstandingAmount },
    { label: "Current Order (OSB)", value: remainingAmount },
    { label: "Order cancel amount", value: receivedAmount },
    { label: "Excess Amount", value: refundAmount() },
  ];

  const isDeductDisabled = outstandingAmount === 0 || !deductDetails || deductDetails.length === 0 || deductDetails.every((d) => !d.value || d.value === 0);
  const refundOptions = [
    { label: "Refund", value: "refund", disabled: false },
    { label: "Deduct from Outstanding", value: "deduct_from_due", disabled: isDeductDisabled },
  ];

  return (
    <ModalWrapper title="Cancel Order" onClose={onClose} showCloseButton width="max-w-lg">
      <p className="text-lg text-center">
        This action will cancel the order. <br /> Click Confirm to Cancel the Order
      </p>
      {receivedAmount > 0 && (
        <div className="flex flex-col items-center justify-center h-full mt-4">
          <div className="flex flex-row items-center gap-4">
            {refundOptions.map((option) => (
              <div key={option.value} className={`flex items-center gap-2 ${option.disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                <input
                  type="radio"
                  name="refundOption"
                  value={option.value}
                  checked={cancelData === option.value}
                  onChange={() => onCancelDataChange(option.value)}
                  className="w-6 h-6"
                  disabled={option.disabled}
                />
                <p>{option.label}</p>
              </div>
            ))}
          </div>

          {cancelData === "refund" && (
            <div>
              <div className="mt-4 flex justify-center">
                <div className="grid grid-cols-[220px_20px_140px] text-red-600 font-semibold text-2xl">
                  <span>Refund amount</span>
                  <span className="text-center">:</span>
                  <span className="text-right">{receivedAmount} QAR</span>
                </div>
              </div>
              {ledgerReceivedAmount > 0 && (
                <div className="mt-3 border border-gray-300 bg-yellow-50 rounded-md p-3 text-sm">
                  <strong>Note:</strong> Any amount paid through the Ledger page will be applied on the outstanding balance. To refund that amount, please use the <strong>Refund</strong> option in the{" "}
                  <strong>Ledger page</strong>.
                </div>
              )}
            </div>
          )}

          {cancelData === "deduct_from_due" && (
            <div className="mt-4 space-y-2 font-semibold w-80">
              {deductDetails.map((item, index) => (
                <div key={index} className="flex justify-between w-full">
                  <span className="w-[65%] text-left">{item.label}</span>
                  <span className="w-[5%] text-center">:</span>
                  <span className="w-[40%] text-right">{item.value} QAR</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-4 mt-6">
        <CommonButtons className="w-36" label="Cancel" variant="outlineDanger" onClick={onClose} />
        <CommonButtons className="w-36" label="Confirm" onClick={onConfirmCancel} />
      </div>
    </ModalWrapper>
  );
};

export default CancelOrderDialog;
